import React from 'react';
import { User } from '@supabase/supabase-js';
import { Contact } from '../../../types/Contact';
import { ArrowLeft, Search, Share2, FolderPlus, Loader2, Image as ImageIcon, FileDown, Download } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { addHistory } from '../../../lib/history';
import { exportContactToPDF, exportContactToHTML } from '../../../utils/contactExportUtils';

interface ContactHeaderProps {
    contact: Contact;
    user: User | null;
    onBack: () => void;
    searchOpen: boolean;
    setSearchOpen: (open: boolean) => void;
    setIsShareOpen: (open: boolean) => void;
    setIsCollectionModalOpen: (open: boolean) => void;
    onAddPhoto?: () => void;
    setAddDetailsOpen: (open: boolean) => void;
    addDetailsOpen?: boolean;
    searchQuery: string;
    setSearchQuery: (query: string) => void;
    searchResponse: string;
    searchLoading: boolean;
    handleSearch: () => Promise<void>;
    children?: React.ReactNode;
}

export default function ContactHeader({
    contact,
    user,
    onBack,
    searchOpen,
    setSearchOpen,
    setIsShareOpen,
    setIsCollectionModalOpen,
    onAddPhoto,
    setAddDetailsOpen,
    addDetailsOpen,
    searchQuery,
    setSearchQuery,
    searchResponse,
    searchLoading,
    handleSearch,
    children,
}: ContactHeaderProps) {
    const [detailsText, setDetailsText] = React.useState('');

    const handleSaveDetails = async () => {
        const text = detailsText.trim();
        if (!text || !contact?.id) { setAddDetailsOpen(false); return; }
        try {
            await addHistory({ contact_id: contact.id, actor_id: (user as any)?.id || null, action: 'details_added', details: { text } });
            try { (window as any).dispatchEvent(new CustomEvent('gv:refreshHistory', { detail: { contactId: contact.id } })); } catch { }
            setDetailsText('');
        } catch {
            // non-blocking
        } finally {
            setAddDetailsOpen(false);
        }
    };
    return (
        <>
            {/* Pretty Header Bar */}
            <div className="bg-gradient-to-b from-gray-900 to-black border-b border-gray-800">
                <div className="max-w-4xl mx-auto px-4 py-3">
                    <div className="flex items-center justify-between">
                        {/* Back */}
                        <button
                            onClick={onBack}
                            className="w-9 h-9 bg-gray-800 border border-gray-700 rounded-lg flex items-center justify-center text-white hover:bg-gray-700 hover:border-gray-600 transition-all"
                            aria-label="Back"
                        >
                            <ArrowLeft className="w-5 h-5" />
                        </button>

                        <div className="flex-1" />

                        {/* Header actions */}
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setSearchOpen((v) => { const next = !v; if (next) setAddDetailsOpen(false); return next; })}
                                className="w-9 h-9 bg-gray-800 border border-gray-700 rounded-lg flex items-center justify-center text-white hover:bg-gray-700 hover:border-gray-600 transition-all"
                                aria-label="Search"
                            >
                                <Search className="w-5 h-5" />
                            </button>
                            <button
                                onClick={() => setIsShareOpen(true)}
                                className="w-9 h-9 bg-gray-800 border border-gray-700 rounded-lg flex items-center justify-center text-white hover:bg-gray-700 hover:border-gray-600 transition-all"
                                aria-label="Share"
                            >
                                <Share2 className="w-5 h-5" />
                            </button>
                            {user && contact.id && (
                                <button
                                    onClick={() => setIsCollectionModalOpen(true)}
                                    className="w-9 h-9 bg-gray-800 border border-gray-700 rounded-lg flex items-center justify-center text-white hover:bg-gray-700 hover:border-gray-600 transition-all"
                                    aria-label="Add to Collection"
                                    title="Add to Collection"
                                >
                                    <FolderPlus className="w-5 h-5" />
                                </button>
                            )}
                            {user && contact.id && (
                                <button
                                    onClick={() => onAddPhoto && onAddPhoto()}
                                    className="w-9 h-9 bg-gray-800 border border-gray-700 rounded-lg flex items-center justify-center text-white hover:bg-gray-700 hover:border-gray-600 transition-all"
                                    aria-label="Add Photo"
                                    title="Add Photo"
                                >
                                    <ImageIcon className="w-5 h-5" />
                                </button>
                            )}
                            {user && contact.id && <ExportDropdown contact={contact} />}
                        </div>
                    </div>
                    {/* slot for the top/profile section */}
                    {children ? (
                        <div className="pt-2 pb-1">
                            {children}
                        </div>
                    ) : null}
                </div>
            </div>

            {/* Transitional zone: always-on subtle gradient that intensifies for panels */}
            <div className={`bg-gradient-to-b transition-colors duration-300 ${searchOpen || addDetailsOpen
                ? 'from-black via-gray-900 to-gray-800 pt-4 pb-6 border-b border-gray-800'
                : 'from-gray-900/40 via-gray-900/20 to-gray-800/40 py-2'
                }`}>
                <div className="max-w-4xl mx-auto px-4 sm:px-6 space-y-4 pb-2">
                    {/* AI Search Panel - card style */}
                    {searchOpen && (
                        <div className="bg-gray-900 border border-gray-700 rounded-xl p-5 mb-2">
                            <div className="relative flex items-center gap-2">
                                <div className="relative flex-1">
                                    <button
                                        onClick={handleSearch}
                                        disabled={searchLoading}
                                        className="absolute left-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-gray-400 hover:text-white hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-0"
                                        aria-label="Ask"
                                        title="Ask"
                                    >
                                        {searchLoading ? (
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : (
                                            <Search className="w-5 h-5" />
                                        )}
                                    </button>
                                    <input
                                        type="text"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        placeholder="Ask anything..."
                                        className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 text-white placeholder-gray-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-600 focus:border-gray-600"
                                        onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                                    />
                                </div>
                            </div>
                            {searchResponse && (
                                <div className="mt-3 bg-gray-800 p-4 rounded-lg border border-gray-700">
                                    <p className="text-gray-300 whitespace-pre-line">{searchResponse}</p>
                                </div>
                            )}
                        </div>
                    )}
                    {/* Add Details Panel */}
                    {addDetailsOpen && (
                        <div className="bg-gray-900 border border-gray-700 rounded-xl p-5">
                            <div className="flex items-center justify-between mb-2">
                                <h4 className="text-sm font-semibold text-white">Add Details</h4>
                                <button
                                    onClick={() => setAddDetailsOpen(false)}
                                    className="px-2 py-1 text-sm rounded-md border border-gray-700 text-gray-300 hover:bg-gray-700"
                                    aria-label="Close"
                                    title="Close"
                                >
                                    Close
                                </button>
                            </div>
                            <textarea
                                value={detailsText}
                                onChange={(e) => setDetailsText(e.target.value)}
                                placeholder="Add details about this contact..."
                                rows={4}
                                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-gray-200 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600"
                            />
                            <div className="flex justify-end gap-2 pt-3">
                                <button
                                    type="button"
                                    onClick={() => { setDetailsText(''); setAddDetailsOpen(false); }}
                                    className="px-3 py-1.5 text-sm rounded-md border border-gray-700 text-gray-300 hover:bg-gray-700"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={handleSaveDetails}
                                    disabled={!detailsText.trim()}
                                    className="px-3 py-1.5 text-sm rounded-md border border-gray-700 bg-gray-800 hover:bg-gray-700 text-white disabled:opacity-50"
                                >
                                    Save
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}

// Export Dropdown Component
interface ExportDropdownProps {
    contact: Contact;
}

function ExportDropdown({ contact }: ExportDropdownProps) {
    const [isOpen, setIsOpen] = React.useState(false);
    const [isExporting, setIsExporting] = React.useState(false);
    const dropdownRef = React.useRef<HTMLDivElement>(null);

    // Close dropdown when clicking outside
    React.useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleExportPDF = async () => {
        setIsExporting(true);
        setIsOpen(false);
        try {
            await exportContactToPDF(contact);
            // Note: Success message will be shown in the print dialog
        } catch (error) {
            console.error('Export failed:', error);
            alert(`Export failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
        } finally {
            setIsExporting(false);
        }
    };

    const handleExportHTML = async () => {
        setIsExporting(true);
        setIsOpen(false);
        try {
            await exportContactToHTML(contact);
            // File download will be handled automatically
        } catch (error) {
            console.error('Export failed:', error);
            alert(`Export failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
        } finally {
            setIsExporting(false);
        }
    };

    const contactName = `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || 'Contact';

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                disabled={isExporting}
                className="w-9 h-9 bg-gray-800 border border-gray-700 rounded-lg flex items-center justify-center text-white hover:bg-gray-700 hover:border-gray-600 transition-all disabled:opacity-50"
                aria-label="Export Contact"
                title="Export Contact"
            >
                {isExporting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                    <FileDown className="w-5 h-5" />
                )}
            </button>

            {isOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-50">
                    <div className="py-2">
                        <div className="px-3 py-2 text-xs text-gray-400 border-b border-gray-700">
                            Export {contactName}
                        </div>
                        <button
                            onClick={handleExportPDF}
                            className="w-full px-3 py-2 text-left text-sm text-white hover:bg-gray-700 transition-colors flex items-center gap-2"
                        >
                            <FileDown className="w-4 h-4" />
                            Export as PDF
                            <span className="ml-auto text-xs text-gray-400">Print/Save</span>
                        </button>
                        <button
                            onClick={handleExportHTML}
                            className="w-full px-3 py-2 text-left text-sm text-white hover:bg-gray-700 transition-colors flex items-center gap-2"
                        >
                            <Download className="w-4 h-4" />
                            Export as HTML
                            <span className="ml-auto text-xs text-gray-400">Download</span>
                        </button>
                        <div className="px-3 py-2 text-xs text-gray-400 border-t border-gray-700">
                            📄 AI-optimized format with all contact data, events, relationships, and business info for comprehensive analysis
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}